import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tagsArea", "checklist", "checkbox", "noTagsMsg", "newTagInput", "tagError", "deleteTagName",
                    "unshareTagName", "unshareFriendName"]
  static values = { slug: String, addUrl: String, removeUrl: String, createUrl: String,
                    tagUpdateUrl: String, tagDeleteUrl: String, shareUrl: String, unshareUrl: String,
                    searchUrl: String }

  connect() {
    this.modal = null
    this.confirmModal = null
    this.sharedTagsModal = null
    this.unshareConfirmModal = null
    this.pendingDeleteTagId = null
    this.pendingUnshareId = null
    this.pendingUnshareChip = null
    const modalEl = document.getElementById("contentTagsModal")
    if (modalEl) this.modal = new bootstrap.Modal(modalEl)
    const confirmModalEl = document.getElementById("tagDeleteConfirmModal")
    if (confirmModalEl) this.confirmModal = new bootstrap.Modal(confirmModalEl)
    const sharedTagsModalEl = document.getElementById("sharedTagsModal")
    if (sharedTagsModalEl) this.sharedTagsModal = new bootstrap.Modal(sharedTagsModalEl)
    const unshareConfirmModalEl = document.getElementById("tagUnshareConfirmModal")
    if (unshareConfirmModalEl) this.unshareConfirmModal = new bootstrap.Modal(unshareConfirmModalEl)
  }

  openModal() {
    if (this.modal) this.modal.show()
  }

  openSharedTagsModal() {
    if (this.modal) this.modal.hide()
    if (this.sharedTagsModal) this.sharedTagsModal.show()
  }

  closeSharedTagsModal() {
    if (this.sharedTagsModal) this.sharedTagsModal.hide()
    if (this.modal) this.modal.show()
  }

  toggleTag(event) {
    const checkbox = event.currentTarget
    const tagId = checkbox.dataset.tagId

    if (checkbox.checked) {
      this.applyTag(tagId, checkbox)
    } else {
      this.unapplyTag(tagId, checkbox)
    }
  }

  applyTag(tagId, checkbox) {
    const url = this.addUrlValue.replace(":tag_id", tagId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(url, { method: "POST", headers: { "X-CSRF-Token": csrfToken } })
      .then(r => r.json())
      .then(data => {
        if (!data.success && checkbox) {
          checkbox.checked = false
        } else {
          this.updateTagsArea()
          this.updateEpisodeButtons(data.has_shared_tags)
        }
      })
  }

  unapplyTag(tagId, checkbox) {
    const url = this.removeUrlValue.replace(":tag_id", tagId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(url, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } })
      .then(r => r.json())
      .then(data => {
        if (!data.success && checkbox) {
          checkbox.checked = true
        } else {
          this.updateTagsArea()
          this.updateEpisodeButtons(data.has_shared_tags)
        }
      })
  }

  updateEpisodeButtons(hasSharedTags) {
    if (hasSharedTags) {
      document.querySelectorAll(".episode-watch-btn").forEach(btn => {
        const sharedBtn = document.createElement("button")
        sharedBtn.type = "button"
        sharedBtn.className = "btn btn-link text-secondary p-0 ms-1 shared-watch-btn"
        sharedBtn.title = "Watch with Friends"
        sharedBtn.innerHTML = '<i class="bi bi-people"></i>'
        btn.replaceWith(sharedBtn)
      })
    } else {
      document.querySelectorAll(".shared-watch-btn").forEach(btn => {
        const episodeBtn = document.createElement("button")
        episodeBtn.type = "button"
        episodeBtn.className = "btn btn-link text-secondary p-0 ms-1 episode-watch-btn"
        episodeBtn.title = "Watch/Rate"
        episodeBtn.innerHTML = '<i class="bi bi-star"></i>'
        btn.replaceWith(episodeBtn)
      })
    }

    document.dispatchEvent(new CustomEvent("content-tags:buttons-changed"))
  }

  createTag() {
    const input = this.newTagInputTarget
    const name = input.value.trim()
    if (!name) return

    const csrfToken = document.querySelector("[name='csrf-token']").content
    this.tagErrorTarget.classList.add("d-none")

    fetch(this.createUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ name })
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) {
          this.tagErrorTarget.textContent = data.errors?.join(", ") || "Failed to create tag"
          this.tagErrorTarget.classList.remove("d-none")
          return
        }
        input.value = ""
        const checkbox = this.addCheckboxToModal(data.tag)
        this.applyTag(data.tag.id, checkbox)
      })
  }

  startEdit(event) {
    const tagId = event.currentTarget.dataset.tagId
    const row = this.checklistTarget.querySelector(`[data-tag-row="${tagId}"]`)
    row.querySelector("[data-tag-label]").classList.add("d-none")
    const input = row.querySelector("[data-tag-edit-input]")
    input.classList.remove("d-none")
    input.focus()
    row.querySelectorAll("[data-tag-view]").forEach(el => el.classList.add("d-none"))
    row.querySelectorAll("[data-tag-edit-btn]").forEach(el => el.classList.remove("d-none"))
  }

  cancelEdit(event) {
    const tagId = event.currentTarget.dataset.tagId
    const row = this.checklistTarget.querySelector(`[data-tag-row="${tagId}"]`)
    const label = row.querySelector("[data-tag-label]")
    row.querySelector("[data-tag-edit-input]").value = label.textContent.trim()
    row.querySelector("[data-tag-edit-input]").classList.add("d-none")
    label.classList.remove("d-none")
    row.querySelectorAll("[data-tag-view]").forEach(el => el.classList.remove("d-none"))
    row.querySelectorAll("[data-tag-edit-btn]").forEach(el => el.classList.add("d-none"))
  }

  saveEdit(event) {
    const tagId = event.currentTarget.dataset.tagId
    const row = this.checklistTarget.querySelector(`[data-tag-row="${tagId}"]`)
    const input = row.querySelector("[data-tag-edit-input]")
    const newName = input.value.trim()
    if (!newName) return

    const url = this.tagUpdateUrlValue.replace(":id", tagId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ name: newName })
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return
        const label = row.querySelector("[data-tag-label]")
        label.textContent = data.tag.name
        input.value = data.tag.name
        const checkbox = row.querySelector("[data-content-tags-target='checkbox']")
        if (checkbox) checkbox.dataset.tagName = data.tag.name
        const deleteBtn = row.querySelector("[data-action*='confirmDelete']")
        if (deleteBtn) deleteBtn.dataset.tagName = data.tag.name
        input.classList.add("d-none")
        label.classList.remove("d-none")
        row.querySelectorAll("[data-tag-view]").forEach(el => el.classList.remove("d-none"))
        row.querySelectorAll("[data-tag-edit-btn]").forEach(el => el.classList.add("d-none"))
        this.updateTagsArea()
      })
  }

  confirmDelete(event) {
    const btn = event.currentTarget
    this.pendingDeleteTagId = btn.dataset.tagId
    if (this.hasDeleteTagNameTarget) this.deleteTagNameTarget.textContent = btn.dataset.tagName
    if (this.confirmModal) this.confirmModal.show()
  }

  executeDelete() {
    if (!this.pendingDeleteTagId) return
    const tagId = this.pendingDeleteTagId
    const url = this.tagDeleteUrlValue.replace(":id", tagId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(url, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return
        const row = this.checklistTarget.querySelector(`[data-tag-row="${tagId}"]`)
        if (row) row.remove()
        this.pendingDeleteTagId = null
        if (this.confirmModal) this.confirmModal.hide()
        this.updateTagsArea()
      })
  }

  addCheckboxToModal(tag) {
    let container = this.checklistTarget.querySelector(".mb-3")
    if (!container) {
      container = document.createElement("div")
      container.className = "mb-3"
      if (this.hasNoTagsMsgTarget) {
        this.noTagsMsgTarget.replaceWith(container)
      } else {
        this.checklistTarget.prepend(container)
      }
    }

    const div = document.createElement("div")
    div.className = "d-flex align-items-center gap-2 py-1"
    div.dataset.tagRow = tag.id
    div.innerHTML = `
      <input class="form-check-input flex-shrink-0 mt-0" type="checkbox"
             id="tag_${tag.id}" value="${tag.id}" checked
             data-content-tags-target="checkbox"
             data-tag-id="${tag.id}" data-tag-name="${tag.name}"
             data-action="change->content-tags#toggleTag">
      <label class="form-check-label flex-grow-1" for="tag_${tag.id}" data-tag-label>${tag.name}</label>
      <input type="text" class="form-control form-control-sm flex-grow-1 d-none"
             data-tag-edit-input data-tag-id="${tag.id}" value="${tag.name}"
             data-action="keydown.enter->content-tags#saveEdit keydown.escape->content-tags#cancelEdit">
      <div class="d-flex gap-1 flex-shrink-0">
        <button type="button" class="btn btn-sm p-1 lh-1 btn-secondary" data-tag-view
                data-tag-id="${tag.id}" data-action="click->content-tags#startEdit">
          <i class="bi bi-pencil"></i>
        </button>
        <button type="button" class="btn btn-sm p-1 lh-1 btn-success d-none" data-tag-edit-btn
                data-tag-id="${tag.id}" data-action="click->content-tags#saveEdit">
          <i class="bi bi-check-lg"></i>
        </button>
        <button type="button" class="btn btn-sm p-1 lh-1 btn-secondary d-none" data-tag-edit-btn
                data-tag-id="${tag.id}" data-action="click->content-tags#cancelEdit">
          <i class="bi bi-x-lg"></i>
        </button>
        <button type="button" class="btn btn-sm p-1 lh-1 btn-danger" data-tag-view
                data-tag-id="${tag.id}" data-tag-name="${tag.name}"
                data-action="click->content-tags#confirmDelete">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    `
    container.appendChild(div)
    return div.querySelector("input[type='checkbox']")
  }

  updateTagsArea() {
    const checkedBoxes = this.checkboxTargets.filter(cb => cb.checked)
    const tagsArea = this.tagsAreaTarget

    if (checkedBoxes.length === 0) {
      tagsArea.innerHTML = `<button type="button" class="btn btn-tag btn-action-sm" data-action="click->content-tags#openModal"><i class="bi bi-tags me-1"></i>Add Tags</button>`
    } else {
      tagsArea.innerHTML = checkedBoxes.map(cb =>
        `<span class="badge content-user-tag-badge me-1 mb-1" data-tag-id="${cb.dataset.tagId}" data-action="click->content-tags#openModal"><i class="bi bi-tag-fill me-1"></i>${cb.dataset.tagName}</span>`
      ).join("")
    }
  }

  searchShareUser(event) {
    const input = event.currentTarget
    const tagId = input.dataset.tagId
    const query = input.value.trim()
    const resultsContainer = this.element.querySelector(`[data-share-results-for="${tagId}"]`)
    if (!resultsContainer) return

    if (query.length < 2) {
      resultsContainer.innerHTML = ""
      resultsContainer.classList.remove("show")
      return
    }

    const sharedUserIds = new Set(
      Array.from(this.element.querySelectorAll(`[data-shared-users-for="${tagId}"] span`))
           .map(span => span.dataset.userId)
           .filter(Boolean)
    )

    fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(r => r.json())
      .then(data => {
        const unshared = (data.users || []).filter(u => !sharedUserIds.has(String(u.id)))

        if (unshared.length === 0) {
          resultsContainer.innerHTML = `<span class="dropdown-item disabled text-muted small">No friends found</span>`
          resultsContainer.classList.add("show")
          return
        }

        resultsContainer.innerHTML = unshared.map(u =>
          `<button type="button" class="dropdown-item"
                   data-tag-id="${tagId}"
                   data-user-id="${u.id}"
                   data-user-name="${u.name}"
                   data-action="click->content-tags#selectShareUser">${u.name}</button>`
        ).join("")
        resultsContainer.classList.add("show")
      })
  }

  clearShareSearch(event) {
    const input = event.currentTarget
    const tagId = input.dataset.tagId
    input.value = ""
    const resultsContainer = this.element.querySelector(`[data-share-results-for="${tagId}"]`)
    if (resultsContainer) {
      resultsContainer.innerHTML = ""
      resultsContainer.classList.remove("show")
    }
  }

  selectShareUser(event) {
    const btn = event.currentTarget
    const tagId = btn.dataset.tagId
    const userId = btn.dataset.userId
    const userName = btn.dataset.userName
    const csrfToken = document.querySelector("[name='csrf-token']").content

    const resultsContainer = this.element.querySelector(`[data-share-results-for="${tagId}"]`)
    if (resultsContainer) {
      resultsContainer.innerHTML = ""
      resultsContainer.classList.remove("show")
    }

    const searchInput = this.element.querySelector(`input[data-tag-id="${tagId}"][data-action*="searchShareUser"]`)
    if (searchInput) searchInput.value = ""

    fetch(this.shareUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ tag_id: tagId, shared_with_user_id: userId })
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return
        const row = this.element.querySelector(`[data-shared-tag-row="${tagId}"]`)
        const tagName = row?.dataset.tagName || ""
        const chipsContainer = this.element.querySelector(`[data-shared-users-for="${tagId}"]`)
        if (!chipsContainer) return

        const chip = document.createElement("span")
        chip.className = "badge bg-secondary d-flex align-items-center gap-1"
        chip.dataset.userId = userId
        chip.innerHTML = `${userName}
          <button type="button" class="btn-close btn-close-white tag-share-remove-btn"
                  aria-label="Remove"
                  data-shared-tag-id="${data.shared_tag_id}"
                  data-tag-id="${tagId}"
                  data-tag-name="${tagName}"
                  data-friend-name="${userName}"
                  data-action="click->content-tags#confirmUnshare"></button>`
        chipsContainer.appendChild(chip)
      })
  }

  confirmUnshare(event) {
    const btn = event.currentTarget
    this.pendingUnshareId = btn.dataset.sharedTagId
    this.pendingUnshareChip = btn.closest("span")
    if (this.hasUnshareTagNameTarget) this.unshareTagNameTarget.textContent = btn.dataset.tagName
    if (this.hasUnshareFriendNameTarget) this.unshareFriendNameTarget.textContent = btn.dataset.friendName
    if (this.unshareConfirmModal) this.unshareConfirmModal.show()
  }

  executeUnshare() {
    if (!this.pendingUnshareId) return
    const sharedTagId = this.pendingUnshareId
    const chip = this.pendingUnshareChip
    const url = this.unshareUrlValue.replace(":id", sharedTagId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(url, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } })
      .then(r => r.json())
      .then(data => {
        if (data.success && chip) chip.remove()
        this.pendingUnshareId = null
        this.pendingUnshareChip = null
        if (this.unshareConfirmModal) this.unshareConfirmModal.hide()
      })
  }
}
